import { useState } from 'react'
import { X } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/ui'
import { useTranslation } from '@/core'

interface IdoneityUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patientName: string
  companyName: string
  onSubmit: (input: { file: File; notes: string }) => Promise<void>
  isPending: boolean
}

export const IdoneityUploadDialog = ({
  open,
  onOpenChange,
  patientName,
  companyName,
  onSubmit,
  isPending,
}: IdoneityUploadDialogProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [notes, setNotes] = useState('')
  const { t } = useTranslation()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0])
      e.target.value = ''
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!file) return
    try {
      await onSubmit({ file, notes })
      onOpenChange(false)
      setFile(null)
      setNotes('')
    } catch {
      // The PageGenerator mutation surfaces the request error.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{t('booking.idoneityUploadTitle')}</DialogTitle>
          <DialogDescription>
            {t('booking.idoneityUploadDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
            <div>
              <span className="text-xs font-medium text-blue-700">
                {t('booking.patient')}
              </span>
              <p className="text-sm text-blue-900">{patientName}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-blue-700">
                {t('booking.company')}
              </span>
              <p className="text-sm text-blue-900">{companyName}</p>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file">{t('documents.files')}</Label>
            <Input
              id="file"
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            {file && (
              <div className="space-y-2 mt-2">
                <div className="bg-secondary text-secondary-foreground px-3 py-2 rounded-md text-sm flex items-center gap-2">
                  <span className="max-w-[250px] truncate">{file.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-auto hover:bg-destructive/10 hover:text-destructive"
                    onClick={handleRemoveFile}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('documents.notes')}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('booking.idoneityNotesPlaceholder')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isPending || !file}>
              {isPending ? t('common.uploading') : t('booking.idoneitySubmit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
